import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from '../firebase/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Create context
const CartContext = createContext();

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return {
        ...state,
        items: action.payload,
        loading: false
      };
    case 'ADD_ITEM':
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id
      );
      
      if (existingItemIndex >= 0) {
        // Item exists, update quantity with stock validation
        const updatedItems = [...state.items];
        const newQuantity = updatedItems[existingItemIndex].quantity + action.payload.quantity;
        const maxStock = action.payload.stock || 0;
        
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: Math.min(newQuantity, maxStock),
          stock: maxStock
        };
        return {
          ...state,
          items: updatedItems
        };
      } else {
        // New item, add to cart with stock validation
        const maxStock = action.payload.stock || 0;
        return {
          ...state,
          items: [...state.items, {
            ...action.payload,
            quantity: Math.min(action.payload.quantity, maxStock)
          }]
        };
      }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item => {
          if (item.id === action.payload.id) {
            const maxStock = item.stock || 0;
            return { 
              ...item, 
              quantity: Math.min(action.payload.quantity, maxStock)
            };
          }
          return item;
        })
      };
    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
};

// Provider component
export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const initialState = {
    items: [],
    loading: true
  };
  
  const [state, dispatch] = useReducer(cartReducer, initialState);
  
  // Load cart from localStorage on initial render
  useEffect(() => {
    const loadCart = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        if (currentUser) {
          // User is logged in, try to get cart from Firestore
          const cartDoc = await getDoc(doc(db, 'carts', currentUser.uid));
          
          if (cartDoc.exists()) {
            // Cart exists in Firestore
            const cartData = cartDoc.data();
            dispatch({ type: 'SET_CART', payload: cartData.items || [] });
          } else {
            // No cart in Firestore, check localStorage
            const localCart = localStorage.getItem('cart');
            
            if (localCart) {
              const parsedCart = JSON.parse(localCart);
              dispatch({ type: 'SET_CART', payload: parsedCart });
              
              // Save localStorage cart to Firestore
              await setDoc(doc(db, 'carts', currentUser.uid), {
                items: parsedCart,
                updatedAt: new Date()
              });
            } else {
              // No cart anywhere
              dispatch({ type: 'SET_CART', payload: [] });
            }
          }
        } else {
          // User not logged in, use localStorage only
          const localCart = localStorage.getItem('cart');
          
          if (localCart) {
            dispatch({ type: 'SET_CART', payload: JSON.parse(localCart) });
          } else {
            dispatch({ type: 'SET_CART', payload: [] });
          }
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        // Fallback to empty cart
        dispatch({ type: 'SET_CART', payload: [] });
      }
    };
    
    loadCart();
  }, [currentUser]);
  
  // Save cart to localStorage and Firestore when it changes
  useEffect(() => {
    if (!state.loading) {
      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(state.items));
      
      // Save to Firestore if user is logged in
      if (currentUser) {
        const saveToFirestore = async () => {
          try {
            const cartRef = doc(db, 'carts', currentUser.uid);
            const cartDoc = await getDoc(cartRef);
            
            if (cartDoc.exists()) {
              // Update existing cart
              await updateDoc(cartRef, {
                items: state.items,
                updatedAt: new Date()
              });
            } else {
              // Create new cart
              await setDoc(cartRef, {
                items: state.items,
                updatedAt: new Date()
              });
            }
          } catch (error) {
            console.error('Error saving cart to Firestore:', error);
          }
        };
        
        saveToFirestore();
      }
    }
  }, [state.items, state.loading, currentUser]);
  
  // Calculate cart totals
  const getCartTotals = () => {
    return state.items.reduce(
      (totals, item) => {
        const itemTotal = item.price * item.quantity;
        return {
          subtotal: totals.subtotal + itemTotal,
          itemCount: totals.itemCount + item.quantity
        };
      },
      { subtotal: 0, itemCount: 0 }
    );
  };
  
  // Add item to cart
  const addToCart = (item) => {
    // Stock should already be passed in the item object from the component
    const stock = item.stock || 0;
    
    if (stock <= 0) {
      console.warn('Product is out of stock');
      return;
    }
    
    dispatch({ type: 'ADD_ITEM', payload: { ...item, stock } });
  };
  
  // Remove item from cart
  const removeFromCart = (itemId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
  };
  
  // Update item quantity
  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
    } else {
      // Get the item from cart to check its stock
      const cartItem = state.items.find(item => item.id === itemId);
      const stock = cartItem?.stock || 0;
      
      if (quantity > stock) {
        console.warn(`Cannot add more than ${stock} items - only ${stock} in stock`);
      }
      
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { id: itemId, quantity: Math.min(quantity, stock) }
      });
    }
  };
  
  // Clear cart
  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };
  
  return (
    <CartContext.Provider
      value={{
        cart: state.items,
        loading: state.loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotals
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
